import React, { FC, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { existsAndNotEmpty } from "stentor-utils";
import { TitleHeader, Tooltip, Button, PageAlert } from "@xapp/components";

import { AppReferenceIdInput, IntegrationType } from "generated/graphql";

import {
    IntegrationCardLayoutContext,
    IntegrationDetailsContext,
    IntegrationHealthContext,
    IntegrationStatusFilter,
} from "../integrationCardContext";
import { rowStatus, STATUS_FILTERS, STATUS_META } from "../integrationStatus";
import { IntegrationDeliveryHealth, IntegrationDetailsDialog, IntegrationDetailsDialogProps } from "./IntegrationDetailsDialog";
import { IntegrationsListDialog } from "./IntegrationsListDialog";
import { leadConnectRemoval } from "./LeadConnectIntegrationCard";
import { LeadConnectDialogContainer } from "../containers/LeadConnectDialogContainer";
import { IntegrationList, IntegrationListProps } from "./IntegrationList";
import { IntegrationConnectionProps } from "./BasicIntegrationCard";
import { ReferenceIdSettings } from "./ReferenceIdSettings";
import { SearchLightConnectionProps } from "./SearchLightIntegrationCard";
import { ServiceTitanAppUpgradeBanner } from "./ServiceTitanAppUpgradeBanner";

import style from "./style.scss";

export interface IntegrationViewProps extends IntegrationListProps {
    /**
     * Add Integration dialog is active
     */
    active?: boolean;
    /**
     * User has requested to add an integration. (SHOW THE DIALOG)
     */
    onAdd?(): void;
    /**
     * Dialog cancel
     */
    onCancel?(): void;
    /**
     * User is attempting to connect an integration
     *
     * This is used for the non-OAUTH integrations: ServiceTitan, Surefire, Housecall Pro
     */
    onConnect?(props: IntegrationConnectionProps): void;
    /**
     * User requested to disconnect the integration.
     */
    onDisconnect?(type: IntegrationType): void;
    /**
     * Reference ID for external service
     */
    referenceId?: AppReferenceIdInput | null;
    /**
     * Whether the reference ID is currently being saved
     */
    savingReferenceId?: boolean;
    /**
     * Callback to save the reference ID
     */
    onSaveReferenceId?(referenceId: AppReferenceIdInput | null): void;
    /** Delivery health per connected integration (App.integrationHealth) */
    health?: IntegrationDeliveryHealth[];
    /** Sends failed leads to an integration again, from its details */
    onResend?: IntegrationDetailsDialogProps["onResend"];
}

export const IntegrationsView: FC<IntegrationViewProps> = (props: IntegrationViewProps) => {
    const {
        active,
        app,
        data,
        limitIntegrations,
        omitIntegrations,
        onAdd,
        onCancel,
        onSelected,
        selected,
        supportsIntegrations,
        referenceId,
        savingReferenceId,
        onSaveReferenceId,
        health,
        onResend,
    } = props;

    const selectionMode = existsAndNotEmpty(selected);
    // The connected integration whose details are open
    const [detailsFor, setDetailsFor] = useState<string>();
    const [filter, setFilter] = useState<IntegrationStatusFilter>("ALL");
    // Disconnecting a bought Call Connect from its details asks to cancel it instead
    const [cancellingLeadConnect, setCancellingLeadConnect] = useState(false);
    const leadConnectRemove = leadConnectRemoval(data?.addOns?.leadConnect?.status, data?.addOns?.leadConnect?.source);
    const healthById = useMemo(
        () => Object.fromEntries((health || []).map((h) => [h.integrationId, h])) as Record<string, IntegrationDeliveryHealth>,
        [health]
    );

    if (selectionMode) {
        // make sure we have a callback
        if (typeof onSelected !== "function") {
            throw new Error("onSelected must be a function when selected array is provided");
        }
    }

    // What does linked and active mean?
    // isLinked - User has set up a link to jobber
    // isActive - The link works

    const isBoberdooLinked: boolean = data?.boberdoo?.isLinked ?? false;
    const isFree2GrowLinked: boolean = data?.free2grow?.isLinked ?? false;
    const isLeadPerfectionLinked: boolean = data?.leadperfection?.isLinked ?? false;
    const isLACRMLinked: boolean = data?.lacrm?.isLinked;
    const isJobberLinked: boolean = data?.jobber?.isLinked;
    const isServiceTitanLinked: boolean = data?.servicetitan?.isLinked;
    // Only a confirmed refusal asks for the upgrade; null means reconciliation has not checked yet
    const isServiceTitanUpgradeRequired: boolean = !!isServiceTitanLinked && data?.servicetitan?.invoicesRead === false;
    const isHousecallProLinked: boolean = data?.housecallpro?.isLinked;
    const isFieldPulseLinked: boolean = data?.fieldpulse?.isLinked;
    const isFieldRoutesLinked: boolean = data?.fieldroutes?.isLinked;
    const isHubspotLinked: boolean = data?.hubspot?.isLinked;
    const isJobNimbusLinked: boolean = data?.jobnimbus?.isLinked ?? false;
    const isSurefireLocalLinked: boolean = data?.surefire?.isLinked;
    const isServiceminderLinked: boolean = data?.serviceminder?.isLinked;
    const isContractorAppointmentsLinked: boolean = data?.contractor_appointments?.isLinked;
    const isWebhookLinked: boolean = data?.webhook?.isLinked;
    const isSearchLightLinked: boolean = data?.searchLightFeed?.enabled ?? false;
    const isLeadConnectLinked: boolean = data?.leadconnect?.isLinked ?? false;

    // If limitIntegrations is null/undefined, allow all integrations
    // If it's an array, use those specific integrations
    let availableIntegrations: string[];

    if (limitIntegrations === null || limitIntegrations === undefined) {
        // Allow all integrations when null/undefined
        availableIntegrations = [
            "boberdoo",
            "free2grow",
            "leadperfection",
            "lacrm",
            "jobber",
            "surefire",
            "servicetitan",
            "serviceminder",
            "housecallpro",
            "zapier",
            "fieldpulse",
            "fieldroutes",
            "hubspot",
            "jobnimbus",
            "contractor_appointments",
            "webhook",
            "searchlight",
            "leadconnect",
        ];
    } else if (Array.isArray(limitIntegrations)) {
        // Use the specific allowed integrations. Lead Connect is a paid add-on bought on its own,
        // not part of the plan's integration list, so it is offered either way.
        availableIntegrations = limitIntegrations.includes("leadconnect")
            ? limitIntegrations
            : [...limitIntegrations, "leadconnect"];
    } else {
        // Fallback to default list if something unexpected
        availableIntegrations = [
            "boberdoo",
            "free2grow",
            "leadperfection",
            "lacrm",
            "jobber",
            "surefire",
            "servicetitan",
            "serviceminder",
            "housecallpro",
            "zapier",
            "fieldpulse",
            "fieldroutes",
            "hubspot",
            "jobnimbus",
            "contractor_appointments",
            "webhook",
            "searchlight",
            "leadconnect",
        ];
    }

    if (existsAndNotEmpty(omitIntegrations)) {
        // Create a set for faster lookup
        const omitSet = new Set(omitIntegrations);

        // Filter out the omitted integrations
        availableIntegrations = availableIntegrations.filter((integration) => {
            if (omitSet.has(integration)) {
                return false;
            }
            return true;
        });
    }

    const linkedIntegrations: string[] = [];

    if (isBoberdooLinked) {
        linkedIntegrations.push("boberdoo");
    }

    if (isFree2GrowLinked) {
        linkedIntegrations.push("free2grow");
    }

    if (isLeadPerfectionLinked) {
        linkedIntegrations.push("leadperfection");
    }

    if (isLACRMLinked) {
        linkedIntegrations.push("lacrm");
    }

    if (isJobberLinked) {
        linkedIntegrations.push("jobber");
    }

    if (isServiceTitanLinked) {
        linkedIntegrations.push("servicetitan");
    }

    if (isHousecallProLinked) {
        linkedIntegrations.push("housecallpro");
    }

    if (isFieldPulseLinked) {
        linkedIntegrations.push("fieldpulse");
    }

    if (isFieldRoutesLinked) {
        linkedIntegrations.push("fieldroutes");
    }

    if (isHubspotLinked) {
        linkedIntegrations.push("hubspot");
    }

    if (isJobNimbusLinked) {
        linkedIntegrations.push("jobnimbus");
    }

    if (isSurefireLocalLinked) {
        linkedIntegrations.push("surefire");
    }

    if (isServiceminderLinked) {
        linkedIntegrations.push("serviceminder");
    }

    if (isContractorAppointmentsLinked) {
        linkedIntegrations.push("contractor_appointments");
    }

    if (isWebhookLinked) {
        linkedIntegrations.push("webhook");
    }

    if (isSearchLightLinked) {
        linkedIntegrations.push("searchlight");
    }

    if (isLeadConnectLinked) {
        linkedIntegrations.push("leadconnect");
    }

    const hasLinkedIntegration: boolean = existsAndNotEmpty(linkedIntegrations);

    // Each connected integration's tone, for the filter chips' counts. The SearchLight feed has no
    // connection of its own: it is working while it is on.
    const isActiveFor = (id: string): boolean | null | undefined =>
        id === "searchlight" ? data?.searchLightFeed?.enabled : (data as Record<string, { isActive?: boolean | null } | undefined>)?.[id]?.isActive;
    const toneCounts: Record<string, number> = { ALL: linkedIntegrations.length };
    for (const id of linkedIntegrations) {
        const tone = STATUS_META[rowStatus(isActiveFor(id), healthById[id])].tone;
        toneCounts[tone] = (toneCounts[tone] || 0) + 1;
    }
    // A chip disappears once nothing has its status (a resend fixed the last failing one): show
    // every row again rather than an empty list under a chip that is no longer there.
    const activeFilter: IntegrationStatusFilter = filter === "ALL" || toneCounts[filter] ? filter : "ALL";

    // Filter out already linked integrations from the dialog
    const unlinkedIntegrations = availableIntegrations.filter(
        (integration) => !linkedIntegrations.includes(integration)
    );

    return (
        <>
            <TitleHeader value={"Integrations"}>
                <Tooltip content={supportsIntegrations ? "Add Integration" : "Not Supported"}>
                    <Button
                        data-add-integration
                        variation="primary"
                        label="Add integration"
                        onClick={onAdd}
                        disabled={!supportsIntegrations}
                    />
                </Tooltip>
            </TitleHeader>
            {!supportsIntegrations ? (
                <div>
                    <PageAlert background="orange">
                        <p>
                            Integrations are not supported on your current plan. Please upgrade to one of our paid tiers
                            to integrate with 3rd party service providers.
                        </p>
                        <div style={{ display: "flex", flexDirection: "column", rowGap: "10px", alignItems: "center" }}>
                            <Link to={`/${app.organizationId}/${app.appId}/upgrade`}>
                                <Button variation="primary" label="Upgrade Plan" />
                            </Link>
                        </div>
                    </PageAlert>
                </div>
            ) : (
                <></>
            )}

            <ServiceTitanAppUpgradeBanner upgradeRequired={isServiceTitanUpgradeRequired} />

            {!hasLinkedIntegration ? (
                <div style={{ textAlign: "center", padding: "20px", color: "#666" }}>
                    <p>No integrations connected yet. Click the + button to add one.</p>
                </div>
            ) : (
                <>
                    <div className={style.statusFilters} role="group" aria-label="Filter by status">
                        {STATUS_FILTERS.map(({ key, label }) =>
                            key === "ALL" || toneCounts[key] ? (
                                <button key={key} type="button" aria-pressed={activeFilter === key} onClick={() => setFilter(key)} data-status-filter={key}>
                                    {key !== "ALL" && <span className={`${style.filterDot} ${style[`tone_${key}`]}`} aria-hidden="true" />}
                                    {label} {toneCounts[key] || 0}
                                </button>
                            ) : null
                        )}
                    </div>
                    <IntegrationCardLayoutContext.Provider value="connected">
                        <IntegrationHealthContext.Provider value={{ health: healthById, filter: activeFilter }}>
                            <IntegrationDetailsContext.Provider value={setDetailsFor}>
                                <IntegrationList
                                    {...props}
                                    app={app}
                                    data={data}
                                    onboardingMode={false}
                                    onConnect={props.onConnect}
                                    disabled={!supportsIntegrations}
                                    onDisconnect={props.onDisconnect}
                                />
                            </IntegrationDetailsContext.Provider>
                        </IntegrationHealthContext.Provider>
                    </IntegrationCardLayoutContext.Provider>
                </>
            )}

            <IntegrationsListDialog
                app={app}
                active={active}
                disabled={!supportsIntegrations}
                integrations={unlinkedIntegrations}
                onCancel={onCancel}
                onConnect={props.onConnect}
                onIntegrationChanged={props.onIntegrationChanged}
            />

            <IntegrationDetailsDialog
                integrationId={detailsFor}
                data={data}
                health={detailsFor ? healthById[detailsFor] : undefined}
                organizationId={app?.organizationId}
                appId={app?.appId}
                onResend={onResend}
                onDisconnect={
                    props.onDisconnect && !(detailsFor === "leadconnect" && leadConnectRemove === "none")
                        ? (id) => {
                              setDetailsFor(undefined);
                              if (id === "leadconnect" && leadConnectRemove === "cancel") {
                                  setCancellingLeadConnect(true);
                                  return;
                              }
                              // SearchLight is a feed, not an integration(type:): it has no
                              // disconnect, and goes off the way its own card turns it off.
                              if (id === "searchlight") {
                                  const off: SearchLightConnectionProps = {
                                      integration: "searchlight-feed",
                                      accountId: data?.searchLightFeed?.accountId ?? "",
                                      enabled: false,
                                  };
                                  props.onConnect?.(off);
                                  return;
                              }
                              props.onDisconnect(id as IntegrationType);
                          }
                        : undefined
                }
                onClose={() => setDetailsFor(undefined)}
            />

            {cancellingLeadConnect && app?.appId && (
                <LeadConnectDialogContainer
                    appId={app.appId}
                    organizationId={app.organizationId}
                    active={true}
                    confirmCancelOnOpen={true}
                    onClose={() => setCancellingLeadConnect(false)}
                    onChanged={props.onIntegrationChanged}
                />
            )}

            <ReferenceIdSettings
                referenceId={referenceId}
                saving={savingReferenceId}
                onSave={onSaveReferenceId}
            />
        </>
    );
};
